import { useState, useEffect } from 'react';
import api from '../services/api';
import {
  LuEye, LuFileDown, LuListChecks,
} from 'react-icons/lu';
import { Modal, QUESTION_TYPES, errorMessage, embedUrl } from './trainingUtils';

/**
 * Pré-visualização do curso como o colaborador vai ver — só leitura: não
 * cria matrícula, não registra progresso e as questões vêm SEM gabarito
 * (GET /training/courses/:id/preview).
 */
export default function TrainingPreviewModal({ courseId, onClose }) {
  const [course, setCourse] = useState(null);
  const [selected, setSelected] = useState(null); // { kind, id }
  const [error, setError] = useState('');

  useEffect(() => {
    api.get(`/training/courses/${courseId}/preview`)
      .then(({ data }) => {
        setCourse(data);
        const first = data.modules.find((m) => m.lessons.length)?.lessons[0];
        setSelected(first ? { kind: 'lesson', id: first.id } : null);
      })
      .catch((e) => setError(errorMessage(e, 'Erro ao carregar a pré-visualização')));
  }, [courseId]);

  const lessons = course ? course.modules.flatMap((m) => m.lessons) : [];
  const lesson = selected?.kind === 'lesson' ? lessons.find((l) => l.id === selected.id) : null;
  const assessment = selected?.kind === 'assessment' ? course?.assessments.find((a) => a.id === selected.id) : null;
  const video = lesson && ['video', 'video_text'].includes(lesson.content_type) ? embedUrl(lesson.video_url) : null;

  const navItem = (kind, item, label) => (
    <button key={`${kind}-${item.id}`} type="button"
      className={selected?.kind === kind && selected.id === item.id ? 'is-active' : ''}
      onClick={() => setSelected({ kind, id: item.id })}>
      {label}
    </button>
  );

  return (
    <Modal title="Pré-visualização (como o colaborador vê)" icon={<LuEye color="var(--accent)" />} onClose={onClose} maxWidth={1100}>
      {error && <div className="alert-custom danger">{error}</div>}
      <div className="alert-custom warning" style={{ marginBottom: 0 }}>Modo pré-visualização: nada do que for feito aqui é registrado como progresso.</div>
      {course && (
        <div className="training-preview-layout">
          <nav className="training-preview-nav">
            <div style={{ fontWeight: 700, color: 'var(--text-1)', marginBottom: 6 }}>{course.title}</div>
            {course.sequential && <div className="training-hint" style={{ marginTop: 0 }}>Conclusão sequencial ativada</div>}
            {course.modules.map((m, i) => (
              <div key={m.id}>
                <div className="training-preview-module">Módulo {i + 1}: {m.title}</div>
                {m.lessons.map((l) => navItem('lesson', l, `${l.title}${l.required ? '' : ' (opcional)'}`))}
                {course.assessments.filter((a) => a.module_id === m.id).map((a) => navItem('assessment', a, <><LuListChecks /> {a.title}</>))}
              </div>
            ))}
            {course.assessments.filter((a) => !a.module_id).map((a) => (
              <div key={a.id}>
                <div className="training-preview-module">Prova final</div>
                {navItem('assessment', a, <><LuListChecks /> {a.title}</>)}
              </div>
            ))}
          </nav>

          <div style={{ minWidth: 0 }}>
            {!selected && <div className="training-hint">O curso ainda não tem aulas.</div>}
            {lesson && (
              <div>
                <h3 style={{ fontSize: 18, fontWeight: 700, color: 'var(--text-1)', marginBottom: 12 }}>{lesson.title}</h3>
                {video && (
                  <div className="training-video">
                    {video.type === 'iframe'
                      ? <iframe src={video.src} title={lesson.title} allow="autoplay; encrypted-media; picture-in-picture" allowFullScreen />
                      : <video src={video.src} controls />}
                  </div>
                )}
                {lesson.content_html && (
                  // HTML já sanitizado no backend (sanitize-html) antes de ser gravado.
                  // eslint-disable-next-line react/no-danger
                  <div className="training-rich" dangerouslySetInnerHTML={{ __html: lesson.content_html }} />
                )}
                {lesson.has_material && (
                  <div style={{ marginTop: 14, fontSize: 13 }}><LuFileDown /> Material de apoio: {lesson.material_name}</div>
                )}
                <button type="button" className="training-btn training-btn--primary" style={{ marginTop: 18 }} disabled>Marcar como concluída</button>
              </div>
            )}
            {assessment && (
              <div>
                <h3 style={{ fontSize: 18, fontWeight: 700, color: 'var(--text-1)', marginBottom: 4 }}>{assessment.title}</h3>
                <div className="training-hint" style={{ marginTop: 0, marginBottom: 12 }}>
                  Nota mínima {assessment.passing_score} · {assessment.max_attempts ? `${assessment.max_attempts} tentativa(s)` : 'tentativas ilimitadas'}
                  {assessment.time_limit_minutes ? ` · ${assessment.time_limit_minutes} min` : ''}
                </div>
                {assessment.description && <p style={{ fontSize: 14 }}>{assessment.description}</p>}
                {assessment.questions.map((q, i) => (
                  <div key={q.id} className="training-block" style={{ padding: 14 }}>
                    <div style={{ fontWeight: 600, color: 'var(--text-1)', marginBottom: 8 }}>{i + 1}. {q.statement}
                      <span className="training-hint" style={{ marginLeft: 8 }}>{QUESTION_TYPES[q.type]} · peso {q.weight}</span>
                    </div>
                    {q.options.map((o) => (
                      <label key={o.id} className="training-check" style={{ marginBottom: 6 }}>
                        <input type={q.type === 'multiple' ? 'checkbox' : 'radio'} name={`q-${q.id}`} /> {o.text}
                      </label>
                    ))}
                  </div>
                ))}
                <button type="button" className="training-btn training-btn--primary" disabled>Enviar respostas</button>
              </div>
            )}
          </div>
        </div>
      )}
    </Modal>
  );
}
